import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  forwardRef,
  HostListener,
  inject,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { AsyncPipe, NgFor, NgIf, SlicePipe } from '@angular/common';
import { PhoneNumberDropdownItem } from '@csspension/reactive-form/lib/components/reactive-form-item/reactive-form-phone-country-code/phone-number-item';
import { PhoneCountryCodeService } from '../phone-country-code.service';
import { map } from 'rxjs/operators';
import { NgbDropdown, NgbDropdownItem, NgbDropdownMenu, NgbDropdownToggle } from '@ng-bootstrap/ng-bootstrap';
import { ScreenService } from '../../../../services/screen-service.service';
import { Subscribable } from '../../../../models/base/subscribable';

@Component({
  selector: 'app-phone-country-code-dropdown-input',
  templateUrl: './phone-country-code-dropdown-input.component.html',
  styleUrls: ['./phone-country-code-dropdown-input.component.scss'],
  standalone: true,
  imports: [AsyncPipe, NgFor, NgIf, SlicePipe, NgbDropdown, NgbDropdownToggle, NgbDropdownMenu, NgbDropdownItem],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PhoneCountryCodeDropdownInputComponent),
      multi: true
    }
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PhoneCountryCodeDropdownInputComponent
  extends Subscribable
  implements ControlValueAccessor, OnChanges, OnInit
{
  @Input() countryDropdowns: PhoneNumberDropdownItem[] = [];
  @ViewChild('ngbDropdown') ngbDropdown!: NgbDropdown;

  private cdr = inject(ChangeDetectorRef);
  private phoneCountryCodeService = inject(PhoneCountryCodeService);
  private screenService = inject(ScreenService);
  public value: string = '';
  public selectedCountry: PhoneNumberDropdownItem | null = null;
  public isDisabled: boolean = false;

  public interactionState$ = this.phoneCountryCodeService.interactionState$;

  public iconSrc$ = this.interactionState$.pipe(
    map(state => {
      const icons = {
        default: 'assets/icons/grey/Chevron Down.svg',
        hover: 'assets/icons/dark-grey/Chevron-down.svg',
        focus: 'assets/icons/dark/Chevron Down.svg'
      };

      return this.isDisabled ? icons.default : icons[state];
    })
  );

  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.countryDropdowns) {
      this.syncSelectedCountry(this.value);
    }
  }

  public ngOnInit() {
    this.screenService.trueIsMobile$.subscribeWhileAlive({
      owner: this,
      next: isMobile => {
        if (isMobile) {
          this.ngbDropdown.close();
        }
      }
    });
  }

  @HostListener('mouseenter')
  public onHover() {
    this.phoneCountryCodeService.setIsHovered(true);
  }

  @HostListener('mouseleave')
  public onLeave() {
    this.phoneCountryCodeService.setIsHovered(false);
  }

  public onFocus() {
    this.phoneCountryCodeService.setIsFocused(true);
  }

  public onBlur() {
    this.phoneCountryCodeService.setIsFocused(false);
  }

  public writeValue(value: string): void {
    this.value = value;
    this.syncSelectedCountry(value);
    this.cdr.detectChanges();
  }

  public registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    this.cdr.markForCheck();
  }

  public syncSelectedCountry(value: string): void {
    this.selectedCountry = this.countryDropdowns?.find(option => option.countryCode === value) || null;
  }

  public onCountrySelected(country: PhoneNumberDropdownItem) {
    this.value = country.countryCode;
    this.selectedCountry = country;
    this.onChange(this.value);
    this.onTouched();
    this.cdr.detectChanges();
  }
}
